import React, {useEffect, useState} from 'react';
import {Modal, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {useClubCompare} from '@/Hooks/ClubsApi/useClubsQueries';
import type {CompareSide} from '@/Hooks/ClubsApi/types';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

type Period = 'daily' | 'weekly' | 'all';
type MetricKey =
  | 'rating'
  | 'games_played'
  | 'tournaments_played'
  | 'win_pct'
  | 'puzzles_solved'
  | 'first_attempt_solved';

const PERIODS: {key: Period; label: string}[] = [
  {key: 'daily', label: 'Today'},
  {key: 'weekly', label: 'This week'},
  {key: 'all', label: 'All time'},
];
const METRICS: {
  key: MetricKey;
  label: string;
  format: (value: number | null) => string;
}[] = [
  {
    key: 'rating',
    label: 'Rating',
    format: value => (value == null ? 'Unrated' : String(value)),
  },
  {
    key: 'games_played',
    label: 'Games played',
    format: value => String(value ?? 0),
  },
  {
    key: 'tournaments_played',
    label: 'Tournaments played',
    format: value => String(value ?? 0),
  },
  {
    key: 'win_pct',
    label: 'Win rate',
    format: value => (value == null ? '—' : `${value}%`),
  },
  {
    key: 'puzzles_solved',
    label: 'Puzzles solved',
    format: value => String(value ?? 0),
  },
  {
    key: 'first_attempt_solved',
    label: 'First-attempt solves',
    format: value => String(value ?? 0),
  },
];
const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#67B3E1', '#937ADB', '#D1AB41'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[Math.abs(name.charCodeAt(0)) % AVATAR_COLORS.length];
const displayName = (side: CompareSide) => side.name || side.username;

function Player({side, you}: {side: CompareSide; you?: boolean}) {
  return (
    <View style={styles.player}>
      <View
        style={[styles.avatar, {backgroundColor: avatarColor(side.username)}]}>
        <CCText style={styles.avatarText}>
          {side.username.charAt(0).toUpperCase()}
        </CCText>
      </View>
      <CCText numberOfLines={1} style={styles.playerName}>
        {displayName(side)}
      </CCText>
      <CCText numberOfLines={1} style={styles.username}>
        @{side.username}
      </CCText>
      {you ? (
        <View style={styles.youPill}>
          <CCText style={styles.youText}>YOU</CCText>
        </View>
      ) : null}
    </View>
  );
}

function MetricRow({
  label,
  left,
  right,
  format,
}: {
  label: string;
  left: number | null;
  right: number | null;
  format: (value: number | null) => string;
}) {
  const leftWins = left != null && right != null && left > right;
  const rightWins = left != null && right != null && right > left;
  return (
    <View style={styles.metricRow}>
      <View style={[styles.metricValue, leftWins && styles.winnerValue]}>
        <CCText style={[styles.metricNumber, leftWins && styles.winnerText]}>
          {format(left)}
        </CCText>
      </View>
      <CCText style={styles.metricLabel}>{label}</CCText>
      <View style={[styles.metricValue, rightWins && styles.winnerValue]}>
        <CCText style={[styles.metricNumber, rightWins && styles.winnerText]}>
          {format(right)}
        </CCText>
      </View>
    </View>
  );
}

function CompareSkeleton() {
  return (
    <View testID="club-compare-skeleton" style={styles.skeletonWrap}>
      <View style={styles.skeletonPlayers}>
        <View style={[styles.skeleton, styles.skeletonAvatar]} />
        <View style={[styles.skeleton, styles.skeletonVs]} />
        <View style={[styles.skeleton, styles.skeletonAvatar]} />
      </View>
      {METRICS.map(metric => (
        <View key={metric.key} style={[styles.skeleton, styles.skeletonRow]} />
      ))}
    </View>
  );
}

export default function ClubCompareModal({
  visible,
  clubId,
  rightId,
  onClose,
}: {
  visible: boolean;
  clubId: number;
  rightId: number | null;
  onClose: () => void;
}) {
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const [period, setPeriod] = useState<Period>('all');
  const {data, isLoading, isError, refetch} = useClubCompare(
    clubId,
    undefined,
    rightId ?? 0,
    period,
  );

  useEffect(() => {
    if (visible) setPeriod('all');
  }, [visible, rightId]);

  if (!visible) return null;

  const left = data?.left;
  const right = data?.right;
  const record = data?.head_to_head;
  const statLead =
    left && right
      ? METRICS.reduce(
          (result, metric) => {
            const leftValue = left[metric.key];
            const rightValue = right[metric.key];
            if (leftValue != null && rightValue != null) {
              if (leftValue > rightValue) result.left += 1;
              if (rightValue > leftValue) result.right += 1;
            }
            return result;
          },
          {left: 0, right: 0},
        )
      : null;

  return (
    <Modal
      transparent
      visible={visible}
      animationType={isWide ? 'fade' : 'slide'}
      onRequestClose={onClose}>
      <View style={[styles.backdrop, isWide ? styles.center : styles.bottom]}>
        <Pressable
          testID="club-compare-backdrop"
          accessibilityLabel="Close comparison"
          onPress={onClose}
          style={StyleSheet.absoluteFill}
        />
        <View
          testID="club-compare-modal"
          style={[styles.sheet, isWide ? styles.card : styles.bottomSheet]}>
          {!isWide ? <View style={styles.grabber} /> : null}
          <View style={styles.hero}>
            <View style={styles.heroTop}>
              <View style={styles.heroIcon}>
                <CCText style={styles.heroIconText}>♞</CCText>
              </View>
              <Pressable
                testID="club-compare-close"
                accessibilityLabel="Close comparison"
                onPress={onClose}
                style={styles.close}>
                <CCText style={styles.closeText}>×</CCText>
              </Pressable>
            </View>
            <CCText fontWeight="thunder" style={styles.title}>
              Member match-up
            </CCText>
            <CCText style={styles.subtitle}>
              Compare platform-wide activity and your club head-to-head record.
            </CCText>
            <View style={styles.periodControl}>
              {PERIODS.map(option => {
                const active = period === option.key;
                return (
                  <Pressable
                    key={option.key}
                    testID={`club-compare-period-${option.key}`}
                    onPress={() => setPeriod(option.key)}
                    style={[
                      styles.periodButton,
                      active && styles.periodButtonActive,
                    ]}>
                    <CCText
                      style={[
                        styles.periodText,
                        active && styles.periodTextActive,
                      ]}>
                      {option.label}
                    </CCText>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <ScrollView
            testID="club-compare-scroll"
            style={styles.scroll}
            contentContainerStyle={styles.content}
            scrollEnabled
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled">
            {isLoading ? (
              <CompareSkeleton />
            ) : isError || !left || !right || !record ? (
              <View style={styles.errorCard}>
                <CCText style={styles.errorIcon}>♟</CCText>
                <CCText style={styles.errorTitle}>
                  Comparison unavailable
                </CCText>
                <CCText style={styles.errorText}>
                  We could not load this match-up. Check your connection and try
                  again.
                </CCText>
                <Pressable
                  testID="club-compare-retry"
                  onPress={() => refetch()}
                  style={styles.retry}>
                  <CCText style={styles.retryText}>Try again</CCText>
                </Pressable>
              </View>
            ) : (
              <>
                <View style={styles.matchCard}>
                  <Player side={left} you />
                  <View style={styles.vsBadge}>
                    <CCText style={styles.vsText}>VS</CCText>
                  </View>
                  <Player side={right} />
                </View>

                <View style={styles.insight}>
                  <CCText style={styles.insightIcon}>✦</CCText>
                  <CCText style={styles.insightText}>
                    {statLead!.left === statLead!.right
                      ? `A balanced match-up — ${statLead!.left} stat${
                          statLead!.left === 1 ? '' : 's'
                        } each.`
                      : statLead!.left > statLead!.right
                      ? `You lead ${
                          statLead!.left
                        } of the key stats. Keep the momentum going!`
                      : `${displayName(right)} leads ${
                          statLead!.right
                        } key stats — time for a comeback!`}
                  </CCText>
                </View>

                <View style={styles.sectionHeader}>
                  <CCText style={styles.sectionTitle}>
                    Platform-wide performance
                  </CCText>
                  <CCText style={styles.sectionHint}>
                    All games and puzzles · higher values are highlighted
                  </CCText>
                </View>
                <View style={styles.metricsCard}>
                  {METRICS.map(metric => (
                    <MetricRow
                      key={metric.key}
                      label={metric.label}
                      left={left[metric.key]}
                      right={right[metric.key]}
                      format={metric.format}
                    />
                  ))}
                </View>

                <View style={styles.recordCard}>
                  <CCText style={styles.recordEyebrow}>
                    CLUB HEAD-TO-HEAD RECORD
                  </CCText>
                  <View style={styles.recordGrid}>
                    <View style={styles.recordCell}>
                      <CCText style={styles.recordNumber}>
                        {record.left_wins}
                      </CCText>
                      <CCText style={styles.recordLabel}>Your wins</CCText>
                    </View>
                    <View style={[styles.recordCell, styles.recordMiddle]}>
                      <CCText style={styles.recordNumber}>
                        {record.draws}
                      </CCText>
                      <CCText style={styles.recordLabel}>Draws</CCText>
                    </View>
                    <View style={styles.recordCell}>
                      <CCText style={styles.recordNumber}>
                        {record.right_wins}
                      </CCText>
                      <CCText style={styles.recordLabel}>
                        {displayName(right)} wins
                      </CCText>
                    </View>
                  </View>
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {flex: 1, backgroundColor: 'rgba(38,35,34,0.48)'},
  center: {justifyContent: 'center', alignItems: 'center', padding: 24},
  bottom: {justifyContent: 'flex-end'},
  sheet: {backgroundColor: '#FAF6EB', overflow: 'hidden', minHeight: 0},
  card: {width: '100%', maxWidth: 660, maxHeight: '88%', borderRadius: 24},
  bottomSheet: {
    width: '100%',
    height: '90%',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  grabber: {
    width: 42,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#CFC7B7',
    alignSelf: 'center',
    marginTop: 9,
  },
  hero: {
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 16,
    backgroundColor: '#F3EDDD',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.08)',
  },
  heroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 13,
    backgroundColor: '#3DAB9E',
    justifyContent: 'center',
    alignItems: 'center',
    transform: [{rotate: '-8deg'}],
  },
  heroIconText: {color: '#fff', fontSize: 25, lineHeight: 28},
  close: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeText: {color: '#4D3F37', fontSize: 25, lineHeight: 27},
  title: {color: '#262322', fontSize: 30, lineHeight: 34},
  subtitle: {
    color: '#6B625E',
    fontSize: 13.5,
    lineHeight: 19,
    marginTop: 4,
    maxWidth: 500,
  },
  periodControl: {
    flexDirection: 'row',
    backgroundColor: '#E7DFCD',
    borderRadius: 12,
    padding: 4,
    marginTop: 14,
    alignSelf: 'flex-start',
  },
  periodButton: {paddingVertical: 7, paddingHorizontal: 13, borderRadius: 9},
  periodButtonActive: {backgroundColor: '#3DAB9E'},
  periodText: {color: '#6B584D', fontSize: 12.5, fontWeight: '600'},
  periodTextActive: {color: '#fff'},
  scroll: {flex: 1, minHeight: 0},
  content: {padding: 18, paddingBottom: 28},
  matchCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.08)',
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 3},
    shadowOpacity: 0.07,
    shadowRadius: 10,
    elevation: 2,
  },
  player: {flex: 1, minWidth: 0, alignItems: 'center'},
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 7,
  },
  avatarText: {color: '#fff', fontSize: 18, fontWeight: '700'},
  playerName: {
    color: '#262322',
    fontSize: 14,
    fontWeight: '700',
    maxWidth: '100%',
  },
  username: {color: '#888280', fontSize: 11.5, marginTop: 2, maxWidth: '100%'},
  youPill: {
    backgroundColor: '#E4F6F2',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 5,
  },
  youText: {
    color: '#287C72',
    fontSize: 8.5,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  vsBadge: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#FFF2C4',
    borderWidth: 1,
    borderColor: '#F0CF58',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 8,
  },
  vsText: {color: '#806234', fontSize: 11, fontWeight: '800'},
  insight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: '#EAF7F4',
    borderRadius: 12,
    padding: 12,
    marginTop: 12,
  },
  insightIcon: {color: '#3DAB9E', fontSize: 17},
  insightText: {
    flex: 1,
    color: '#35665F',
    fontSize: 12.5,
    lineHeight: 18,
    fontWeight: '600',
  },
  sectionHeader: {marginTop: 20, marginBottom: 9},
  sectionTitle: {color: '#262322', fontSize: 15, fontWeight: '700'},
  sectionHint: {color: '#888280', fontSize: 11.5, marginTop: 2},
  metricsCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.08)',
  },
  metricRow: {
    minHeight: 54,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.07)',
  },
  metricValue: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 7,
    borderRadius: 9,
  },
  winnerValue: {backgroundColor: '#E5F6F2'},
  metricNumber: {color: '#4D3F37', fontSize: 14, fontWeight: '700'},
  winnerText: {color: '#278579'},
  metricLabel: {
    width: 126,
    color: '#888280',
    fontSize: 11.5,
    fontWeight: '600',
    textAlign: 'center',
  },
  recordCard: {
    backgroundColor: '#FFF7D8',
    borderRadius: 16,
    padding: 15,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#ECD278',
  },
  recordEyebrow: {
    color: '#8A6C2B',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.7,
    textAlign: 'center',
    marginBottom: 12,
  },
  recordGrid: {flexDirection: 'row'},
  recordCell: {flex: 1, alignItems: 'center', paddingHorizontal: 4},
  recordMiddle: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: 'rgba(128,98,52,0.18)',
  },
  recordNumber: {color: '#4D3F37', fontSize: 23, fontWeight: '800'},
  recordLabel: {
    color: '#806234',
    fontSize: 10.5,
    textAlign: 'center',
    marginTop: 2,
  },
  errorCard: {
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 24,
    borderRadius: 16,
  },
  errorIcon: {fontSize: 34},
  errorTitle: {color: '#262322', fontSize: 16, fontWeight: '700', marginTop: 8},
  errorText: {
    color: '#888280',
    fontSize: 12.5,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: 5,
    maxWidth: 330,
  },
  retry: {
    backgroundColor: '#3DAB9E',
    borderRadius: 999,
    paddingHorizontal: 20,
    paddingVertical: 9,
    marginTop: 14,
  },
  retryText: {color: '#fff', fontSize: 13, fontWeight: '700'},
  skeletonWrap: {gap: 10},
  skeletonPlayers: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 18,
  },
  skeleton: {backgroundColor: '#E9E2D2', opacity: 0.8},
  skeletonAvatar: {width: 52, height: 52, borderRadius: 26},
  skeletonVs: {width: 38, height: 18, borderRadius: 9},
  skeletonRow: {height: 54, borderRadius: 12},
});
